import { ListingResource } from '../system/field';
import { useReso } from '../contexts/ResoContext';
import { useFieldTranslator } from './FieldTranslator';
import { TaskManager } from './TaskManager';
import {
  ResoResourceBase,
  ResoResourceManager,
  ResourceManagerOptions,
} from './types';
import { MediaResourceManager } from './resources/MediaResource';
import { OpenHouseResourceManager } from './resources/OpenHouseResource';
import { GreenVerificationResourceManager } from './resources/GreenVerificationResource';
import { RoomResourceManager } from './resources/RoomResource';
import { UnitResourceManager } from './resources/UnitResource';
import { AgentResourceManager } from './resources/AgentResource';
import { ResoResource } from '../system/reso';
import { AreaResourceManager } from './resources/AreaResource';
import { CityZipCodeResourceManager } from './resources/CityZipCodeResource';
import { SchoolResourceManager } from './resources/SchoolResource';
import { SchoolDistrictResourceManager } from './resources/SchoolDistrictResource';
import { SubdivisionResourceManager } from './resources/SubdivisionResource';
import { BuildingNameResourceManager } from './resources/BuildingNameResource';

/**
 * Factory for creating resource managers
 */
export class ResourceManagerFactory {
  /**
   * Create a resource manager for a given resource type
   */
  public static createResourceManager<T extends ResoResourceBase>(
    resourceType: ListingResource | string,
    executeQuery: ReturnType<typeof useReso>['executeQuery'],
    taskManager: TaskManager,
    fieldTranslator: ReturnType<typeof useFieldTranslator>,
    options: ResourceManagerOptions = {},
  ): ResoResourceManager<T> {
    // Create the appropriate manager based on resource type
    switch (resourceType) {
      case ListingResource.Media:
        return new MediaResourceManager(
          ListingResource.Media,
          executeQuery,
          fieldTranslator,
          taskManager,
          options,
        ) as unknown as ResoResourceManager<T>;

      case ListingResource.OpenHouse:
        return new OpenHouseResourceManager(
          ListingResource.OpenHouse,
          executeQuery,
          fieldTranslator,
          taskManager,
          options,
        ) as unknown as ResoResourceManager<T>;

      case ListingResource.GreenVerification:
        // TODO: This might need to be fixed
        return new GreenVerificationResourceManager(
          executeQuery,
          fieldTranslator,
          taskManager,
          options,
        ) as unknown as ResoResourceManager<T>;

      case ListingResource.Room:
        return new RoomResourceManager(
          ListingResource.Room,
          executeQuery,
          fieldTranslator,
          taskManager,
          options,
        ) as unknown as ResoResourceManager<T>;

      case ListingResource.Unit:
        return new UnitResourceManager(
          ListingResource.Unit,
          executeQuery,
          fieldTranslator,
          taskManager,
          options,
        ) as unknown as ResoResourceManager<T>;

      case 'Agent':
        return new AgentResourceManager(
          executeQuery,
          fieldTranslator,
          taskManager,
          options,
        ) as unknown as ResoResourceManager<T>;

      case ResoResource.AREA:
        return new AreaResourceManager(
          executeQuery,
          fieldTranslator,
          taskManager,
          options,
        ) as unknown as ResoResourceManager<T>;

      case ResoResource.BUILDING_NAME:
        return new BuildingNameResourceManager(
          executeQuery,
          fieldTranslator,
          taskManager,
          options,
        ) as unknown as ResoResourceManager<T>;

      case ResoResource.SUBDIVISION:
        return new SubdivisionResourceManager(
          executeQuery,
          fieldTranslator,
          taskManager,
          options,
        ) as unknown as ResoResourceManager<T>;

      case ResoResource.SCHOOL_DISTRICT:
        return new SchoolDistrictResourceManager(
          executeQuery,
          fieldTranslator,
          taskManager,
          options,
        ) as unknown as ResoResourceManager<T>;

      case ResoResource.SCHOOL:
        return new SchoolResourceManager(
          executeQuery,
          fieldTranslator,
          taskManager,
          options,
        ) as unknown as ResoResourceManager<T>;

      case ResoResource.CITY_ZIP_CODE:
        return new CityZipCodeResourceManager(
          executeQuery,
          fieldTranslator,
          taskManager,
          options,
        ) as unknown as ResoResourceManager<T>;

      default:
        throw new Error(`Unsupported resource type: ${resourceType}`);
    }
  }
}
